/**
 * Public Gallery Page - Future-Proof Cake Portfolio Showcase
 * 
 * Features:
 * - Search functionality
 * - Sorting options
 * - Infinite scroll pagination
 * - Enhanced lightbox with navigation
 * - Scalable for 1000+ images
 * - Better performance with lazy loading
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { usePageSeo } from '../utils/seo';
import { DEFAULT_OG_IMAGE, PAGE_SEO } from '../utils/seoDefaults';
import { useSiteImageOverrides } from '../hooks/useSiteImageOverrides';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HiSearch, HiX, HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import ShareButton from '../components/ShareButton';

interface GalleryImage {
  id: string;
  title: string | null;
  description: string | null;
  image_url: string;
  category: string;
  is_featured: boolean;
}

type SortOption = 'newest' | 'oldest' | 'category' | 'title';

const ITEMS_PER_PAGE = 24; // Load 24 images at a time

export default function Gallery() {
  const { getImage } = useSiteImageOverrides();
  usePageSeo({
    ...PAGE_SEO.gallery,
    og: {
      type: 'website',
      title: PAGE_SEO.gallery.title,
      description: PAGE_SEO.gallery.description,
      image: getImage('og_image', DEFAULT_OG_IMAGE),
    },
  });
  const [allImages, setAllImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Fetch gallery images
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch('/api/gallery');
        if (!response.ok) throw new Error('Failed to load gallery');
        const data = await response.json();
        setAllImages(data.images || []);
      } catch (err) {
        console.error('Error loading gallery:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  // Process images: filter, sort
  const processedImages = useMemo(() => {
    let result = [...allImages];

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(img => img.category === selectedCategory);
    }

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(img =>
        (img.title || '').toLowerCase().includes(term) ||
        (img.description || '').toLowerCase().includes(term) ||
        img.category.toLowerCase().includes(term)
      );
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          // Assuming images are returned in creation order (newest first)
          // If you have a created_at field, use that instead
          return 0; // Keep original order (newest first from API)
        case 'oldest':
          return 0; // Reverse would be needed if we had timestamps
        case 'category':
          return a.category.localeCompare(b.category);
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        default:
          return 0;
      }
    });

    return result;
  }, [allImages, selectedCategory, searchTerm, sortBy]);

  // Get unique categories
  const categories = useMemo(() => {
    return ['all', ...new Set(allImages.map(img => img.category))].sort();
  }, [allImages]);

  // Displayed images (pagination)
  const displayedImages = useMemo(() => {
    return processedImages.slice(0, displayedCount);
  }, [processedImages, displayedCount]);

  const hasMore = displayedCount < processedImages.length;

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setDisplayedCount(prev => Math.min(prev + ITEMS_PER_PAGE, processedImages.length));
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, processedImages.length]);

  // Reset displayed count when filters change
  useEffect(() => {
    setDisplayedCount(ITEMS_PER_PAGE);
  }, [selectedCategory, searchTerm, sortBy]);

  // Featured images for hero
  const featuredImages = useMemo(() => {
    return allImages.filter(img => img.is_featured).slice(0, 4);
  }, [allImages]);

  // Lightbox navigation
  const currentImageIndex = useMemo(() => {
    if (!selectedImage) return -1;
    return processedImages.findIndex(img => img.id === selectedImage.id);
  }, [selectedImage, processedImages]);

  const navigateLightbox = useCallback((direction: 'prev' | 'next') => {
    if (currentImageIndex === -1) return;
    const newIndex = direction === 'next'
      ? (currentImageIndex + 1) % processedImages.length
      : (currentImageIndex - 1 + processedImages.length) % processedImages.length;
    setSelectedImage(processedImages[newIndex]);
  }, [currentImageIndex, processedImages]);

  // Keyboard navigation in lightbox
  useEffect(() => {
    if (!selectedImage) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
      if (e.key === 'ArrowRight') navigateLightbox('next');
      if (e.key === 'Escape') setSelectedImage(null);
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage, navigateLightbox]);

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  const hasActiveFilters = searchTerm.trim() || selectedCategory !== 'all' || sortBy !== 'newest';

  return (
    <div className="bg-gradient-to-b from-bakery-cream to-white">
      {/* Hero Section */}
      <section className="gradient-primary text-white section-padding">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-display font-bold mb-4"
          >
            Our Creations
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-100 max-w-2xl mx-auto"
          >
            Browse our portfolio of custom cakes and treats for inspiration
          </motion.p>
        </div>
      </section>

      {/* Featured Section */}
      {featuredImages.length > 0 && (
        <section className="section-padding bg-white">
          <div className="container-custom">
            <h2 className="text-3xl font-display font-bold text-gray-900 mb-6 text-center">
              ⭐ Featured Creations
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {featuredImages.map((image, index) => (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative aspect-square rounded-2xl overflow-hidden cursor-pointer group"
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                    src={image.image_url}
                    alt={image.title || 'Featured cake'}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                    <p className="text-white font-medium">{image.title || image.category}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Search and Filters Bar */}
      {!loading && allImages.length > 0 && (
        <section className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
          <div className="container-custom py-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search gallery..."
                  className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <HiX className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                aria-label="Filter by category"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </option>
                ))}
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="px-4 py-2.5 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                aria-label="Sort images"
              >
                <option value="newest">Sort: Newest</option>
                <option value="oldest">Sort: Oldest</option>
                <option value="category">Sort: Category</option>
                <option value="title">Sort: Title</option>
              </select>
            </div>

            {/* Results Count */}
            {hasActiveFilters && (
              <div className="mt-3 text-sm text-gray-600">
                Showing {displayedImages.length} of {processedImages.length} image{processedImages.length !== 1 ? 's' : ''}
                {searchTerm && ` matching "${searchTerm}"`}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Loading State */}
      {loading && (
        <section className="section-padding">
          <div className="container-custom text-center py-12">
            <div className="animate-spin w-10 h-10 border-4 border-primary-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading our beautiful creations...</p>
          </div>
        </section>
      )}

      {/* Empty State */}
      {!loading && allImages.length === 0 && (
        <section className="section-padding">
          <div className="container-custom text-center py-12">
            <div className="text-6xl mb-4">🎂</div>
            <h2 className="text-2xl font-display font-bold text-gray-900">Gallery Coming Soon</h2>
            <p className="text-gray-600 mt-2 mb-6">We're uploading photos of our beautiful creations!</p>
            <Link to="/contact" className="btn-primary">
              Contact Us About Custom Cakes
            </Link>
          </div>
        </section>
      )}

      {/* Gallery Grid */}
      {!loading && allImages.length > 0 && (
        <section className="section-padding">
          <div className="container-custom">
            {processedImages.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg mb-2">No images found</p>
                <p className="text-gray-400 text-sm">
                  {hasActiveFilters ? 'Try adjusting your search or filters.' : 'Check back soon for new photos!'}
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('all');
                      setSortBy('newest');
                    }}
                    className="mt-4 btn-primary"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {displayedImages.map((image, index) => (
                    <motion.div
                      key={image.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: Math.min(index * 0.03, 0.5) }}
                      className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group bg-gray-100"
                      onClick={() => setSelectedImage(image)}
                    >
                      <img
                        src={image.image_url}
                        alt={image.title || 'Cake creation'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <p className="text-white font-medium text-sm line-clamp-1">{image.title || 'Custom Creation'}</p>
                          <p className="text-white/70 text-xs">{image.category}</p>
                        </div>
                        <div 
                          className="absolute top-2 right-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ShareButton
                            title={image.title || 'Custom Cake'}
                            text={`Look at this beautiful ${image.category} from Nanny B's Bakery! 🎂`}
                            url={`https://nannybbakery.ca/gallery#${image.id}`}
                            size="sm"
                          />
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Infinite Scroll Trigger */}
                {hasMore && (
                  <div ref={observerTarget} className="h-20 flex items-center justify-center">
                    <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full"></div>
                  </div>
                )}

                {/* Load More Button (fallback if intersection observer doesn't work) */}
                {hasMore && (
                  <div className="text-center mt-8">
                    <button
                      onClick={() => setDisplayedCount(prev => Math.min(prev + ITEMS_PER_PAGE, processedImages.length))}
                      className="btn-outline"
                    >
                      Load More ({processedImages.length - displayedCount} remaining)
                    </button>
                  </div>
                )}

                {/* End of Gallery Message */}
                {!hasMore && displayedImages.length > ITEMS_PER_PAGE && (
                  <div className="text-center mt-8 text-gray-500">
                    <p>You've seen all {processedImages.length} image{processedImages.length !== 1 ? 's' : ''}!</p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-white section-padding">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">
            Love What You See?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Every cake is custom made with love. Tell us your vision and we'll create 
            something special for your occasion!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary">
              Request a Custom Cake
            </Link>
          </div>
        </div>
      </section>

      {/* Enhanced Lightbox Modal with Navigation */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative max-w-6xl max-h-[95vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Main Image */}
              <img
                src={selectedImage.image_url}
                alt={selectedImage.title || 'Cake'}
                className="max-w-full max-h-[85vh] rounded-xl object-contain mx-auto"
              />

              {/* Image Info */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 via-black/70 to-transparent rounded-b-xl">
                <h3 className="text-white text-2xl font-bold mb-1">
                  {selectedImage.title || 'Custom Creation'}
                </h3>
                {selectedImage.description && (
                  <p className="text-white/90 mt-1">{selectedImage.description}</p>
                )}
                <p className="text-white/70 text-sm mt-2">{selectedImage.category}</p>
                <p className="text-white/60 text-xs mt-2">
                  {currentImageIndex + 1} of {processedImages.length}
                </p>
              </div>

              {/* Navigation Arrows */}
              {processedImages.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateLightbox('prev');
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
                    aria-label="Previous image"
                  >
                    <HiChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateLightbox('next');
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
                    aria-label="Next image"
                  >
                    <HiChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* Top buttons - Share and Close */}
              <div className="absolute top-4 right-4 flex items-center gap-2">
                <ShareButton
                  title={selectedImage.title || 'Custom Cake'}
                  text={`Look at this beautiful ${selectedImage.category} from Nanny B's Bakery! 🎂`}
                  url={`https://nannybbakery.ca/gallery#${selectedImage.id}`}
                />
                <button
                  onClick={() => setSelectedImage(null)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
                  aria-label="Close lightbox"
                >
                  ✕
                </button>
              </div>

              {/* Keyboard hint */}
              {processedImages.length > 1 && (
                <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/60 text-xs">
                  Use ← → arrow keys to navigate
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
